import { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { adminStorage } from "../../features/admin/service";
import AdminNotificationsCenter from "./AdminNotificationsCenter";
import { AdminToastProvider } from "../../context/AdminToastContext";
import { HUB_TABS } from "./AdminHubTabs";

// Top-level sidebar entries. Grouped areas point at a hub page that renders its
// absorbed pages as in-page sub-tabs (see AdminHubTabs / HUB_TABS), so this list
// stays short and every sub-page keeps its own URL. `hub` links a NAV_ITEMS
// entry to its HUB_TABS key so the sidebar can show a sub-tab count + names.
const NAV_ITEMS: { path: string; label: string; icon: string; hub?: keyof typeof HUB_TABS }[] = [
  { path: "/admin/insights", label: "Insights", hub: "insights", icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" },
  { path: "/admin/users", label: "Users", icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" },
  { path: "/admin/gst-verification", label: "GST Verification", icon: "M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" },
  { path: "/admin/partners", label: "Partners", icon: "M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4 0m8 0a4 4 0 10-4 0M12 14a4 4 0 00-4 4v2h8v-2a4 4 0 00-4-4z" },
  { path: "/admin/deals", label: "Deals", hub: "deals", icon: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" },
  { path: "/admin/channels", label: "Channels", hub: "channels", icon: "M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" },
  { path: "/admin/billing", label: "Billing", hub: "billing", icon: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" },
  { path: "/admin/storefront", label: "Storefront", hub: "storefront", icon: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V7a2 2 0 00-2-2H5a2 2 0 00-2 2v4m14 0H5" },
  { path: "/admin/content", label: "Content", hub: "content", icon: "M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" },
  { path: "/admin/ai-config", label: "AI Config", hub: "aiConfig", icon: "M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" },
  { path: "/admin/enquiries", label: "Enquiries", icon: "M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-6l-4 4v-4z" },
  { path: "/admin/settings", label: "Settings", hub: "settings", icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z" },
];

const AdminLayout: React.FC = () => {
  // Expanded by default only on desktop (>= lg). Tablets start on the icon rail
  // so page content keeps its width; the toggle still expands it manually.
  const [sidebarOpen, setSidebarOpen] = useState(
    () => typeof window === "undefined" || window.innerWidth >= 1024
  );
  // Mobile (< md) renders the sidebar as an overlay drawer, closed by default.
  const [mobileOpen, setMobileOpen] = useState(false);
  // Nav-item hover tooltip: rendered fixed-position (outside the scrolling
  // <nav>) since overflow-y-auto on the nav clips any absolutely-positioned
  // overflow-x content, tooltip included. Shows in both rail and expanded
  // sidebar modes, and lists the hub's sub-tab names when it has any.
  const [navTooltip, setNavTooltip] = useState<{ label: string; subTabs: { path: string; label: string }[]; top: number; left: number } | null>(null);
  const tooltipCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearTooltipClose = () => {
    if (tooltipCloseTimer.current) {
      clearTimeout(tooltipCloseTimer.current);
      tooltipCloseTimer.current = null;
    }
  };
  const scheduleTooltipClose = () => {
    clearTooltipClose();
    tooltipCloseTimer.current = setTimeout(() => setNavTooltip(null), 150);
  };
  const tooltipRef = useRef<HTMLDivElement>(null);
  // Sub-tab lists can be tall (up to 7 items) — anchoring purely off the
  // hovered item's own position pushes the tooltip off-screen (above the top
  // bar, or below the viewport) for nav items near either edge. Clamp it back
  // into view after it renders, once we know its real height.
  useEffect(() => {
    if (!navTooltip || !tooltipRef.current) return;
    const el = tooltipRef.current;
    const rect = el.getBoundingClientRect();
    const topMargin = 72; // stay clear of the sticky top bar
    const bottomMargin = 12;
    let top = rect.top;
    if (rect.bottom > window.innerHeight - bottomMargin) {
      top -= rect.bottom - (window.innerHeight - bottomMargin);
    }
    if (top < topMargin) top = topMargin;
    el.style.top = `${top}px`;
  }, [navTooltip]);
  const location = useLocation();
  const navigate = useNavigate();
  const adminUser = adminStorage.getUser();

  const handleLogout = () => {
    adminStorage.clearSession();
    navigate("/admin/login");
  };

  // Prefix match so a hub stays highlighted while any of its sub-tab routes is
  // open (e.g. /admin/deals/gallery-review highlights "Deals").
  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  // Labels are always visible in the mobile drawer (it overlays, so there is
  // room); on md+ they follow the manual collapse state.
  const labelClass = sidebarOpen ? "" : "md:hidden";

  // Close the mobile drawer on navigation and on Escape.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileOpen]);

  return (
    <AdminToastProvider>
    <div className="min-h-screen bg-gray-950 flex overflow-x-hidden">
      {/* Mobile drawer backdrop */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 z-30"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`w-64 ${sidebarOpen ? "md:w-64" : "md:w-20"} ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0 bg-gray-900 border-r border-gray-800 flex flex-col transition-all duration-300 fixed inset-y-0 left-0 z-40`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-800">
          <span className={`text-lg font-bold bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent ${labelClass}`}>
            Gullivr Admin
          </span>
          {/* Mobile: close drawer */}
          <button
            onClick={() => setMobileOpen(false)}
            className="md:hidden p-1.5 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
            aria-label="Close navigation"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          {/* md+: collapse to icon rail */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden md:block p-1.5 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
            aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={sidebarOpen ? "M11 19l-7-7 7-7m8 14l-7-7 7-7" : "M13 5l7 7-7 7M5 5l7 7-7 7"} />
            </svg>
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {NAV_ITEMS.map((item) => {
            const subTabs = item.hub ? HUB_TABS[item.hub].map((t) => ({ path: t.path, label: t.label })) : [];
            return (
            <Link
              key={item.path}
              to={item.path}
              onMouseEnter={(e) => {
                clearTooltipClose();
                const rect = e.currentTarget.getBoundingClientRect();
                setNavTooltip({
                  label: item.label,
                  subTabs,
                  top: rect.top,
                  left: rect.right + 12,
                });
              }}
              onMouseLeave={scheduleTooltipClose}
              className={`relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group ${
                isActive(item.path)
                  ? "bg-indigo-500/10 text-indigo-400 border border-indigo-500/20"
                  : "text-gray-400 hover:text-white hover:bg-gray-800/60"
              }`}
            >
              <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.icon} />
              </svg>
              <span className={`flex-1 min-w-0 truncate ${labelClass}`}>{item.label}</span>
              {subTabs.length > 0 && (
                <span
                  className={`flex-shrink-0 min-w-[1.25rem] h-5 px-1 rounded-full bg-gray-800 text-gray-400 text-[10px] font-semibold flex items-center justify-center ${labelClass}`}
                >
                  {subTabs.length}
                </span>
              )}
            </Link>
            );
          })}
        </nav>

        {/* Hover tooltip — fixed-position so it escapes the nav's
            overflow-y-auto clipping (which also clips overflow-x). Shows the
            hub's sub-tab names (if any) below the label, in both rail and
            expanded sidebar modes. */}
        {navTooltip && (
          <div
            ref={tooltipRef}
            className="hidden md:block fixed ml-0 px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white text-xs font-medium z-50 shadow-lg max-w-[90vw]"
            style={{ top: navTooltip.top, left: navTooltip.left }}
            onMouseEnter={clearTooltipClose}
            onMouseLeave={scheduleTooltipClose}
          >
            <div className="flex items-center gap-2 whitespace-nowrap">
              <span>{navTooltip.label}</span>
              {navTooltip.subTabs.length > 0 && (
                <span className="text-gray-500 font-normal">{navTooltip.subTabs.length} sub-tabs</span>
              )}
            </div>
            {navTooltip.subTabs.length > 0 && (
              <div className="mt-1.5 pt-1.5 border-t border-gray-700 flex flex-col gap-0.5 max-h-64 overflow-y-auto font-normal">
                {navTooltip.subTabs.map((sub) => (
                  <Link
                    key={sub.path}
                    to={sub.path}
                    onClick={() => setNavTooltip(null)}
                    className="px-2 py-1 rounded bg-gray-700/60 text-gray-300 hover:bg-indigo-500/20 hover:text-indigo-300 transition-colors"
                  >
                    {sub.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* User */}
        <div className="border-t border-gray-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-400 font-semibold text-sm flex-shrink-0">
              {adminUser?.name?.[0]?.toUpperCase() || "A"}
            </div>
            <div className={`flex-1 min-w-0 ${labelClass}`}>
              <p className="text-sm font-medium text-white truncate">{adminUser?.name || "Admin"}</p>
              <p className="text-xs text-gray-500 truncate">{adminUser?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className={`mt-3 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors ${!sidebarOpen ? "md:justify-center" : ""}`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span className={labelClass}>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className={`flex-1 min-w-0 transition-all duration-300 ml-0 ${sidebarOpen ? "md:ml-64" : "md:ml-20"}`}>
        <div className="h-14 flex items-center justify-between gap-2 px-4 sm:px-6 lg:px-8 border-b border-gray-800">
          <button
            onClick={() => setMobileOpen(true)}
            className="md:hidden p-1.5 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
            aria-label="Open navigation"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="flex items-center gap-2 ml-auto">
            <AdminNotificationsCenter />
          </div>
        </div>
        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
    </AdminToastProvider>
  );
};

export default AdminLayout;
